import React, { useState, useEffect } from 'react';
import { fetchExpenses, addExpense } from '../../api/expenses';
import RecentExpensesTable from './RecentExpensesTable';
import MonthlyPaymentsCard from './MonthlyPaymentsCard';
import CategoryChart from './CategoryChart';
import MonthlyExpenseChart from './MonthlyExpenseChart';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const today = new Date().toISOString().slice(0, 10);
const currentMonth = new Date().toISOString().slice(0, 7);
const currentYear = new Date().getFullYear();

// Get remaining days in current month
const getDaysLeftInMonth = () => {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return lastDay - now.getDate();
};

export default function Dashboard({ setShowSetup, saveDefaultBudget, handleQweClick, onBalanceWarning }) {
  const [expenses, setExpenses] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [userDefault, setUserDefault] = useState(null);
  const [dailyExpenses, setDailyExpenses] = useState([]);
  const [monthlyExpenses, setMonthlyExpenses] = useState([]);
  const [yearlyExpenses, setYearlyExpenses] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [dailyAmount, setDailyAmount] = useState(0);
  const [monthlyAmount, setMonthlyAmount] = useState(0);
  const [additionalAmount, setAdditionalAmount] = useState(0);
  const [balanceWarning, setBalanceWarning] = useState(null);

  const fetchYearlyData = (year) => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    const userId = storedUser?.userId;
    const token = localStorage.getItem('token');
    if (!userId || !token) return;
    fetch(`${API}/chartData/${encodeURIComponent(userId)}/${year}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(res => { if (res.success) setYearlyExpenses(res.data || []); })
      .catch(() => {});
  };

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    const userId = storedUser?.userId;
    const token = localStorage.getItem('token');
    if (!userId || !token) return;
    fetch(`${API}/userDefault/${encodeURIComponent(userId)}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => { if (data?.categories) setUserDefault(data); })
      .catch(() => {});
  }, []);

  const fetchChartData = () => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    const userId = storedUser?.userId;
    const token = localStorage.getItem('token');
    if (!userId || !token) return;

    // Fetch today's daily total
    fetch(`${API}/dailyExp/${encodeURIComponent(userId)}/${today}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(res => {
        if (res.success) {
          setDailyExpenses(res.data?.expenses || []);
          setDailyAmount(res.data?.totalAmount || 0);
        }
      })
      .catch(() => {});

    // Fetch this month's total
    fetch(`${API}/monthlyExp/${encodeURIComponent(userId)}/${currentMonth}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(res => {
        if (res.success) {
          setMonthlyExpenses(res.data?.expenses || []);
          setMonthlyAmount(res.data?.totalAmount || 0);
        }
      })
      .catch(() => {});

    // Fetch ALL monthly records for the selected year to build the bar chart
    fetch(`${API}/chartData/${encodeURIComponent(userId)}/${selectedYear}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(res => {
        if (res.success) setYearlyExpenses(res.data || []);
        // Sum additionalAmount across all months for current year
        if (res.success && res.data) {
          const total = res.data.reduce((sum, r) => sum + (r.additionalAmount || 0), 0);
          setAdditionalAmount(total);
        }
      })
      .catch(() => {});
  };

  // Calculate live balance and sustainability warning
  useEffect(() => {
    if (!userDefault?.salary) return;
    const salary    = Number(userDefault.salary);
    const balance   = salary - monthlyAmount - dailyAmount;
    const daysLeft  = getDaysLeftInMonth();
    const expectedDailyBudget = Math.floor(salary / 30);

    if (dailyAmount > 0 && daysLeft > 0 && balance > 0) {
      const actualDaysCanLast = Math.floor(balance / dailyAmount);
      // Warn if: today's spending exceeds expected daily budget AND balance won't last remaining days
      if (dailyAmount > expectedDailyBudget && actualDaysCanLast < daysLeft) {
        const msg = `⚠️ Please consider your spending — at this rate your salary will last only ${actualDaysCanLast} day${actualDaysCanLast !== 1 ? 's' : ''} 😥😥😥`;
        setBalanceWarning(msg);
        onBalanceWarning?.(msg);
      } else {
        setBalanceWarning(null);
        onBalanceWarning?.(null);
      }
    } else if (balance <= 0) {
      const msg = `⚠️ Your balance is Rs. ${balance.toLocaleString('en-LK')} — you have exceeded your salary budget! 😥😥😥`;
      setBalanceWarning(msg);
      onBalanceWarning?.(msg);
    } else {
      setBalanceWarning(null);
      onBalanceWarning?.(null);
    }
  }, [userDefault, dailyAmount, monthlyAmount]);

  const load = async () => {
    try {
      const data = await fetchExpenses();
      setExpenses(data);
    } catch (e) {
      if (!hasLoaded) setExpenses([]);
    }
  };

  useEffect(() => {
    load().then(() => setHasLoaded(true));
    fetchChartData();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleAddExpense = async (expense) => {
    setExpenses(prev => [...prev, expense]);
    try {
      await addExpense(expense);
    } catch (e) {
      console.error(e);
    }
  };

  const updateExpense = (index, updatedExpense) => {
    setExpenses(prev => prev.map((expense, i) =>
      i === index ? { ...expense, ...updatedExpense } : expense
    ));
  };

  const handleQweClickWithRefresh = () => {
    handleQweClick();
    fetchChartData();
  };

  return (
    <div className="dashboard-grid">

      {/* Balance Summary Card */}
      {userDefault?.salary > 0 && (
        <div className="balance-card" style={{ background: 'linear-gradient(135deg, #4682B4 0%, #2c5282 100%)', color: 'white', border: 'none', boxShadow: '0 8px 24px rgba(70,130,180,0.35)' }}>
          <div className="balance-item">
            <span className="balance-label" style={{ color: 'rgba(255,255,255,0.8)' }}>Salary</span>
            <span className="balance-value" style={{ color: '#ffffff', fontWeight: 700 }}>Rs. {Number(userDefault.salary).toLocaleString('en-LK')}</span>
          </div>
          <div className="balance-divider" style={{ background: 'rgba(255,255,255,0.25)' }} />
          <div className="balance-item">
            <span className="balance-label" style={{ color: 'rgba(255,255,255,0.8)' }}>Monthly Spent</span>
            <span className="balance-value" style={{ color: '#fca5a5', fontWeight: 700 }}>- Rs. {monthlyAmount.toLocaleString('en-LK')}</span>
          </div>
          <div className="balance-divider" style={{ background: 'rgba(255,255,255,0.25)' }} />
          <div className="balance-item">
            <span className="balance-label" style={{ color: 'rgba(255,255,255,0.8)' }}>Today Spent</span>
            <span className="balance-value" style={{ color: '#fca5a5', fontWeight: 700 }}>- Rs. {dailyAmount.toLocaleString('en-LK')}</span>
          </div>
          <div className="balance-divider" style={{ background: 'rgba(255,255,255,0.25)' }} />
          <div className="balance-item">
            <span className="balance-label" style={{ color: 'rgba(255,255,255,0.8)' }}>Balance</span>
            <span className="balance-value" style={{ color: '#ffffff', fontWeight: 800, fontSize: '1.2rem' }}>
              Rs. {(Number(userDefault.salary) - monthlyAmount - dailyAmount).toLocaleString('en-LK')}
            </span>
          </div>
        </div>
      )}

      {/* Balance Warning removed — shown in Notifications page instead */}

      <div className="dashboard-bottom">
        <RecentExpensesTable expenses={expenses} setShowSetup={setShowSetup} handleQweClick={handleQweClickWithRefresh} updateExpense={updateExpense} userDefault={userDefault} />
        <MonthlyPaymentsCard expenses={expenses} setShowSetup={setShowSetup} handleQweClick={handleQweClickWithRefresh} updateExpense={updateExpense} userDefault={userDefault} />
      </div>
      <CategoryChart dailyExpenses={dailyExpenses} monthlyExpenses={monthlyExpenses} />
      <MonthlyExpenseChart yearlyExpenses={yearlyExpenses} selectedYear={selectedYear} onYearChange={setSelectedYear} dailyAmount={dailyAmount} monthlyAmount={monthlyAmount} additionalAmount={additionalAmount} />
    </div>
  );
}
